/**
 * ShipmentItemsCatalogModal - Pesquisa de itens da remessa em modal próprio
 * (padrão do catálogo de Produtos e Serviços do Negócio).
 *
 * Abas Kits / Rastreadores / Chips / Item livre, busca, filtro por local, seleção
 * múltipla e "Carregar mais" (get_shipment_stock_candidates v1.2.0, páginas de 30).
 * Prestador destinatário: `excludeProviderId` oculta os itens já no estoque dele
 * (também bloqueado em add_shipment_item).
 *
 * RELEASE: 2026-10-16 v1.2.1 - Serviços não aparecem (filtrados no servidor); linha mostra só o SKU.
 * RELEASE: 2026-10-16 v1.2.0 - Abas "Produtos do negócio" (quando há negócio vinculado) e "Produtos"
 *          (catálogo), com quantidade editável (get_shipment_product_candidates).
 * RELEASE: 2026-10-16 v1.1.0 - Situação de cada item e composição do kit (IMEI/ICCID + situação);
 *          lista recarregada a cada abertura (sem F5).
 * RELEASE: 2026-10-16 v1.0.0 - Substitui a busca embutida (ShipmentItemPicker).
 */
import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ShipmentItemStatusLine } from './ShipmentItemStatus';
import { Loader2, Package, Plus, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FieldLabel } from '@/components/common/FormSection';
import { useShipmentProductCandidates, useShipmentStockCandidates, type ShipmentCandidatePlace, type ShipmentKitComponent, type ShipmentStockCandidate } from '@/hooks/useShipments';
import { stockPlaceTypeLabel, type StockPresentationPlaceType } from '@/lib/stock-presentation';

/** Texto do local atual do equipamento. */
export const shipmentPlaceText = (p: { type: string; label: string | null } | null) =>
  p ? `${stockPlaceTypeLabel(p.type as StockPresentationPlaceType)}${p.label ? ` · ${p.label}` : ''}` : 'Sem local';

/** Item escolhido no catálogo. */
export type CatalogPick =
  | { type: 'tracker' | 'chip' | 'kit'; itemId: string; label: string; place: ShipmentStockCandidate['place']; status?: string | null; components?: ShipmentKitComponent[] | null }
  | { type: 'free'; description: string; quantity: number }
  | { type: 'product'; productId: string; label: string; quantity: number };

type Tab = 'kit' | 'tracker' | 'chip' | 'deal_product' | 'product' | 'free';
const isProductTab = (t: Tab) => t === 'product' || t === 'deal_product';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (picks: CatalogPick[]) => void;
  /** Ids já na remessa/rascunho (não aparecem). */
  hiddenIds?: string[];
  excludeProviderId?: string | null;
  busy?: boolean;
  /** Negócio vinculado à remessa: habilita a aba "Produtos do negócio". */
  dealId?: string | null;
}

export function ShipmentItemsCatalogModal({ open, onOpenChange, onConfirm, hiddenIds = [], excludeProviderId = null, busy, dealId = null }: Props) {
  const [tab, setTab] = useState<Tab>('kit');
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [place, setPlace] = useState<'all' | Exclude<ShipmentCandidatePlace, null>>('all');
  const [selected, setSelected] = useState<Map<string, CatalogPick>>(new Map());
  const [desc, setDesc] = useState('');
  const [qty, setQty] = useState('1');
  const [free, setFree] = useState<CatalogPick[]>([]);

  const qc = useQueryClient();
  // Zera a seleção e descarta a lista em cache a cada abertura
  useEffect(() => {
    if (!open) return;
    qc.invalidateQueries({ queryKey: ['shipment-candidates'] });
    setSelected(new Map()); setFree([]); setSearch(''); setDesc(''); setQty('1');
  }, [open, qc]);

  // Busca com atraso curto
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(search.trim()), 300);
    return () => window.clearTimeout(t);
  }, [search]);

  // Volta para Kits se a aba do negócio deixar de existir
  useEffect(() => { if (tab === 'deal_product' && !dealId) setTab('kit'); }, [tab, dealId]);

  const stockTab = tab !== 'free' && !isProductTab(tab);
  const products = useShipmentProductCandidates(debounced, tab === 'deal_product' ? dealId : null, open && isProductTab(tab));
  const productRows = products.data?.pages.flatMap((p) => p.items) ?? [];
  const toggleProduct = (p: { id: string; name: string; quantity: number }) => setSelected((m) => {
    const n = new Map(m);
    const key = `product:${p.id}`;
    if (n.has(key)) n.delete(key);
    else n.set(key, { type: 'product', productId: p.id, label: p.name, quantity: Math.max(1, Number(p.quantity) || 1) });
    return n;
  });
  const setProductQty = (id: string, value: string) => setSelected((m) => {
    const key = `product:${id}`; const cur = m.get(key);
    if (!cur || cur.type !== 'product') return m;
    const n = new Map(m); n.set(key, { ...cur, quantity: Math.min(100000, Math.max(1, Number(value.replace(/\D/g, '')) || 1)) });
    return n;
  });

  const query = useShipmentStockCandidates(
    stockTab ? (tab as 'kit' | 'tracker' | 'chip') : 'kit', debounced, open && stockTab, excludeProviderId, place === 'all' ? null : place,
  );
  const rows = (query.data?.pages.flat() ?? []).filter((c) => !hiddenIds.includes(c.id));

  const toggle = (c: ShipmentStockCandidate) => setSelected((m) => {
    const n = new Map(m);
    if (n.has(c.id)) n.delete(c.id);
    else n.set(c.id, { type: c.type, itemId: c.id, label: `${c.label}${c.extra ? ` · ${c.extra}` : ''}`, place: c.place, status: c.status ?? null, components: c.components ?? null });
    return n;
  });

  const total = selected.size + free.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-full max-w-full flex-col gap-0 p-0 sm:h-[80vh] sm:max-w-4xl">
        <DialogHeader className="shrink-0 border-b px-6 py-4 text-left">
          <DialogTitle className="flex items-center gap-2"><Package className="h-5 w-5 text-primary" />Adicionar itens à remessa</DialogTitle>
          <DialogDescription>Somente equipamentos ativos, fora de kit, não instalados, fora de transportadora e sem outra remessa aberta.</DialogDescription>
        </DialogHeader>

        <div className="shrink-0 space-y-3 border-b px-6 py-3">
          <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <TabsList>
              <TabsTrigger value="kit">Kits</TabsTrigger>
              <TabsTrigger value="tracker">Rastreadores</TabsTrigger>
              <TabsTrigger value="chip">Chips</TabsTrigger>
              {dealId && <TabsTrigger value="deal_product">Produtos do negócio</TabsTrigger>}
              <TabsTrigger value="product">Produtos</TabsTrigger>
              <TabsTrigger value="free">Item livre</TabsTrigger>
            </TabsList>
          </Tabs>
          {isProductTab(tab) && (
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="h-9 pl-8" value={search} maxLength={60} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar nome ou SKU do produto..." />
            </div>
          )}
          {stockTab && (
            <div className="flex flex-wrap gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input className="h-9 pl-8" value={search} maxLength={60} onChange={(e) => setSearch(e.target.value)}
                  placeholder={tab === 'kit' ? 'Buscar código do kit...' : tab === 'tracker' ? 'Buscar IMEI ou série...' : 'Buscar ICCID ou linha...'} />
              </div>
              <Select value={place} onValueChange={(v) => setPlace(v as typeof place)}>
                <SelectTrigger className="h-9 w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os locais</SelectItem>
                  <SelectItem value="location">Endereço de estoque</SelectItem>
                  <SelectItem value="provider">Prestador</SelectItem>
                  <SelectItem value="none">Sem local</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-3">
          {tab === 'free' ? (
            <div className="space-y-3">
              <div className="grid gap-2 sm:grid-cols-[1fr_100px_auto] sm:items-end">
                <div className="space-y-1.5"><FieldLabel>Descrição</FieldLabel>
                  <Input value={desc} maxLength={200} placeholder="Ex.: Cabo, Manual" onChange={(e) => setDesc(e.target.value)} /></div>
                <div className="space-y-1.5"><FieldLabel>Qtd.</FieldLabel>
                  <Input inputMode="numeric" value={qty} maxLength={6} onChange={(e) => setQty(e.target.value.replace(/\D/g, ''))} /></div>
                <Button type="button" variant="outline" disabled={!desc.trim() || !Number(qty)}
                  onClick={() => { setFree((l) => [...l, { type: 'free', description: desc.trim(), quantity: Number(qty) }]); setDesc(''); setQty('1'); }}>
                  <Plus className="mr-1 h-4 w-4" />Adicionar
                </Button>
              </div>
              {free.map((f, idx) => f.type === 'free' && (
                <div key={idx} className="flex justify-between rounded-md border px-3 py-2 text-sm"><span>{f.description}</span><span className="tabular-nums text-muted-foreground">x{f.quantity}</span></div>
              ))}
            </div>
          ) : isProductTab(tab) ? (
            <div className="divide-y rounded-md border">
              {products.isLoading && <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-muted-foreground" />}
              {!products.isLoading && productRows.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">{tab === 'deal_product' ? 'Nenhum produto no negócio vinculado.' : 'Nenhum produto encontrado.'}</p>
              )}
              {productRows.map((p) => {
                const pick = selected.get(`product:${p.id}`);
                return (
                  <div key={p.id} className="flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-muted/50">
                    <Checkbox checked={!!pick} onCheckedChange={() => toggleProduct(p)} aria-label={`Selecionar ${p.name}`} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{p.name}</p>
                      <p className="truncate text-xs text-muted-foreground">{p.sku ? `SKU ${p.sku}` : 'Produto'}</p>
                    </div>
                    {pick && pick.type === 'product' && (
                      <Input aria-label="Quantidade" className="h-8 w-20" inputMode="numeric" maxLength={6}
                        value={String(pick.quantity)} onChange={(e) => setProductQty(p.id, e.target.value)} />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="divide-y rounded-md border">
              {query.isLoading && <Loader2 className="mx-auto my-6 h-5 w-5 animate-spin text-muted-foreground" />}
              {!query.isLoading && rows.length === 0 && <p className="py-8 text-center text-sm text-muted-foreground">Nenhum item disponível.</p>}
              {rows.map((c) => (
                <label key={c.id} className="flex cursor-pointer items-center gap-3 px-3 py-2.5 text-sm hover:bg-muted/50">
                  <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c)} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{c.label}{c.extra && <span className="font-normal text-muted-foreground"> · {c.extra}</span>}</p>
                    <p className="truncate text-xs text-muted-foreground">{shipmentPlaceText(c.place)}</p>
                    <ShipmentItemStatusLine type={c.type} status={c.status} components={c.components} />
                  </div>
                </label>
              ))}
            </div>
          )}
          {isProductTab(tab) && products.hasNextPage && (
            <div className="mt-3 text-center">
              <Button type="button" variant="ghost" size="sm" disabled={products.isFetchingNextPage} onClick={() => products.fetchNextPage()}>
                {products.isFetchingNextPage && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Carregar mais
              </Button>
            </div>
          )}
          {stockTab && query.hasNextPage && (
            <div className="mt-3 text-center">
              <Button type="button" variant="ghost" size="sm" disabled={query.isFetchingNextPage} onClick={() => query.fetchNextPage()}>
                {query.isFetchingNextPage && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Carregar mais
              </Button>
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 items-center border-t px-6 py-3 sm:justify-between">
          <span className="text-sm text-muted-foreground">{total} selecionado(s)</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button disabled={!total || busy} onClick={() => onConfirm([...selected.values(), ...free])}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Adicionar {total || ''}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
